import { Box, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { localDayKey } from './chatDay'

interface ChatDateDividerProps {
  /** ISO-время первого сообщения дня. */
  iso: string
}

/**
 * Плашка с датой между сообщениями разных суток.
 *
 * День считается в локальной таймзоне браузера — той же, в которой
 * MessageBubble уже показывает время сообщения. Europe/Moscow из utils/date.ts
 * сюда не годится: это таймзона расписания уроков, и у читателя из другого
 * часового пояса плашка разошлась бы с временем в пузыре.
 */
export function ChatDateDivider({ iso }: ChatDateDividerProps) {
  const { t, i18n } = useTranslation()

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        py: 0.75,
        // Клики проходят сквозь полосу плашки к ленте: сама плашка ловит их
        // обратно ниже, чтобы её можно было выделить.
        pointerEvents: 'none',
      }}
    >
      <Chip
        size="small"
        label={formatDividerLabel(iso, i18n.language, t)}
        sx={{ bgcolor: 'background.paper', boxShadow: 1, pointerEvents: 'auto' }}
      />
    </Box>
  )
}

function formatDividerLabel(
  iso: string,
  locale: string,
  t: (key: string) => string,
): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const key = localDayKey(iso)
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)

  if (key === localDayKey(now.toISOString())) return t('chat.dateToday')
  if (key === localDayKey(yesterday.toISOString())) return t('chat.dateYesterday')

  // Год показываем только у прошлых лет: «25 августа» против
  // «25 августа 2025 г.» — в переписке этого года год только мешает.
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date)
}
